<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import CalendarView from './CalendarView.vue'
import ControlsPanel from './ControlsPanel.vue'
import { resolveCompareRange, resolvePreset, startOfDay } from './logic'
import PresetList from './PresetList.vue'
import type {
  CompareModeId,
  DateField,
  DatePickerChange,
  DatePickerChangeReason,
  DatePickerPanelProps,
  DatePreset,
  DatePresetId,
  DateRangeValue,
} from './types'

import { parseLooseDate } from '@/shared/lib/date/parse'
import { dt, useFormat } from '@/shared/lib/format'

const DEFAULT_COMPARE: CompareModeId = 'Previous Period'
const RE_CLICK_RESET_MS = 2000

const props = withDefaults(defineProps<DatePickerPanelProps>(), {
  hideCompare: false,
  disabled: false,
  startPlaceholder: '',
  endPlaceholder: '',
  compareStartPlaceholder: '',
  compareEndPlaceholder: '',
  hidePresetList: false,
  hideControlList: false,
  hideRightPanel: false,
  inputStyle: false,
  compact: false,
  selectablePresets: null,
  embedded: false,
})

const model = defineModel<DateRangeValue>({
  default: (): DateRangeValue => ({
    start: null,
    end: null,
    presetId: null,
    compareEnabled: false,
    compareType: 'Previous Period',
    compareStart: null,
    compareEnd: null,
  }),
})

const emit = defineEmits<{ change: [change: DatePickerChange] }>()
const fmt = useFormat()
const start = ref<Date | null>(null)
const end = ref<Date | null>(null)
const presetId = ref<DatePresetId | null>(null)
const compareEnabled = ref(false)
const compareType = ref<CompareModeId>(DEFAULT_COMPARE)
const customCompareStart = ref<Date | null>(null)
const customCompareEnd = ref<Date | null>(null)
const clickStep = ref(0)
const lastClickAt = ref<number | null>(null)

const minDay = computed(() => (props.min ? startOfDay(props.min) : null))
const maxDay = computed(() => startOfDay(props.max ?? new Date())!)
const compareRange = computed(() =>
  resolveCompareRange(
    compareType.value,
    start.value,
    end.value,
    { start: customCompareStart.value, end: customCompareEnd.value },
    compareEnabled.value,
  ),
)
const showControls = computed(
  () =>
    !props.hideRightPanel &&
    !props.hideControlList &&
    (!props.inputStyle || presetId.value === 'Custom'),
)
const calendarDisabled = computed(
  () =>
    props.disabled ||
    (props.selectablePresets !== null && !props.selectablePresets.includes('Custom')),
)

function display(date: Date | null): string {
  return date ? dt.formatDate(date, fmt.locale) : ''
}

function cloneValue(value: DateRangeValue): DateRangeValue {
  return {
    ...value,
    start: value.start ? new Date(value.start) : null,
    end: value.end ? new Date(value.end) : null,
    compareStart: value.compareStart ? new Date(value.compareStart) : null,
    compareEnd: value.compareEnd ? new Date(value.compareEnd) : null,
  }
}

function hydrate(value: DateRangeValue): void {
  start.value = value.start ? startOfDay(value.start) : null
  end.value = value.end ? startOfDay(value.end) : null
  presetId.value = value.presetId
  compareEnabled.value = value.compareEnabled
  compareType.value = value.compareType
  customCompareStart.value =
    value.compareType === 'Custom' && value.compareStart ? startOfDay(value.compareStart) : null
  customCompareEnd.value =
    value.compareType === 'Custom' && value.compareEnd ? startOfDay(value.compareEnd) : null
}

function publish(reason: DatePickerChangeReason): void {
  const compare = compareRange.value
  const value: DateRangeValue = {
    start: start.value,
    end: end.value,
    presetId: presetId.value,
    compareEnabled: compareEnabled.value,
    compareType: compareType.value,
    compareStart: compare.start,
    compareEnd: compare.end,
  }
  model.value = cloneValue(value)
  emit('change', { reason, value: cloneValue(value) })
}

function selectPreset(preset: DatePreset): void {
  if (props.disabled) return
  clickStep.value = 0
  if (preset.id === 'Custom') {
    presetId.value = 'Custom'
    publish('preset')
    return
  }
  const resolved = resolvePreset(preset.id, new Date(), minDay.value)
  if (!resolved) return
  presetId.value = preset.id
  start.value = resolved.start
  end.value = resolved.end
  publish('preset')
}

function handleDayClick(date: Date): void {
  if (calendarDisabled.value) return
  const clicked = startOfDay(date)!
  if (minDay.value && clicked < minDay.value) return
  presetId.value = 'Custom'
  const now = Date.now()

  if (
    clickStep.value === 2 &&
    (now - (lastClickAt.value ?? 0) > RE_CLICK_RESET_MS || !compareEnabled.value)
  ) {
    clickStep.value = 0
  }

  switch (clickStep.value) {
    case 0:
      start.value = clicked
      end.value = null
      customCompareStart.value = null
      customCompareEnd.value = null
      compareType.value = DEFAULT_COMPARE
      clickStep.value = 1
      break
    case 1:
      if (start.value && clicked < start.value) start.value = clicked
      else {
        end.value = clicked
        clickStep.value = 2
      }
      break
    case 2:
      compareType.value = 'Custom'
      compareEnabled.value = true
      customCompareStart.value = clicked
      customCompareEnd.value = null
      clickStep.value = 3
      break
    case 3:
      if (customCompareStart.value && clicked < customCompareStart.value) {
        customCompareStart.value = clicked
      } else {
        customCompareEnd.value = clicked
        clickStep.value = 0
      }
      break
  }
  lastClickAt.value = now
  publish('calendar')
}

function handleDateInput(payload: { field: DateField; value: string }): void {
  if (calendarDisabled.value) return
  const parsed = parseLooseDate(payload.value)
  if (Number.isNaN(parsed.getTime())) return
  const day = startOfDay(parsed)!
  if ((minDay.value && day < minDay.value) || day > maxDay.value) return
  presetId.value = 'Custom'
  if (payload.field === 'start') start.value = day
  else if (payload.field === 'end') end.value = day
  else {
    compareType.value = 'Custom'
    if (payload.field === 'compareStart') customCompareStart.value = day
    else customCompareEnd.value = day
  }
  publish('input')
}

function toggleCompare(next: boolean): void {
  compareEnabled.value = next
  if (next && compareType.value === 'Custom') compareType.value = DEFAULT_COMPARE
  publish('compare')
}

function selectCompareType(mode: CompareModeId): void {
  compareType.value = mode
  publish('compare')
}

watch(model, (value) => hydrate(value), { deep: true })
onMounted(() => hydrate(model.value))
</script>

<template>
  <div
    class="ui-date-picker-panel inline-flex overflow-hidden rounded-lg border border-border bg-surface font-sans text-fg"
    :class="{
      'is-rail-only': hideRightPanel || hideControlList,
      'is-input-style': inputStyle,
      'is-embedded rounded-none border-0 shadow-none': embedded,
    }"
  >
    <PresetList
      v-if="!hidePresetList"
      :presets="presets"
      :active-preset-id="presetId"
      :compare-modes="compareModes"
      :compare-type="compareType"
      :compare-enabled="compareEnabled"
      :compare-toggle-label="compareToggleLabel"
      :hide-compare="hideCompare"
      :selectable-presets="selectablePresets"
      :disabled="disabled"
      :inline="hideControlList"
      :show-custom="inputStyle"
      @select="selectPreset"
      @update:compare-enabled="toggleCompare"
      @update:compare-type="selectCompareType"
    />
    <div
      v-if="showControls"
      class="ui-date-picker-panel__body flex w-[19.5rem] flex-col p-md"
      :class="{ 'is-disabled pointer-events-none select-none opacity-50': calendarDisabled }"
    >
      <ControlsPanel
        :compare-enabled="compareEnabled"
        :start-input-label="startInputLabel"
        :end-input-label="endInputLabel"
        :compare-start-input-label="compareStartInputLabel"
        :compare-end-input-label="compareEndInputLabel"
        :formatted-start="display(start)"
        :formatted-end="display(end)"
        :formatted-compare-start="display(compareRange.start)"
        :formatted-compare-end="display(compareRange.end)"
        :start-placeholder="startPlaceholder"
        :end-placeholder="endPlaceholder"
        :compare-start-placeholder="compareStartPlaceholder"
        :compare-end-placeholder="compareEndPlaceholder"
        :disabled="calendarDisabled"
        @date-input="handleDateInput"
      />
      <CalendarView
        :weekday-labels="weekdayLabels"
        :month-labels="monthLabels"
        :min="minDay"
        :max="maxDay"
        :start="start"
        :end="end"
        :compare-start="compareRange.start"
        :compare-end="compareRange.end"
        :compare-enabled="compareEnabled"
        @day-click="handleDayClick"
      />
    </div>
  </div>
</template>
